/**
 * Observaciones de la verificación de un texto generado por IA (OT-0049).
 * Un juez revisa todas las oraciones, con o sin cita, contra las fuentes del
 * proyecto; las que conviene confirmar se muestran como observaciones. El
 * autor revisa cada una (corrige la oración o la confirma) y la marca como
 * revisada; una versión no se envía a revisión con observaciones pendientes.
 *
 *   - `GroundingFindingsList` / `GroundingFindingsSection`: el resultado de
 *     "Generar sección" y de "Subsanar con IA", antes de aceptar.
 *   - `SectionObservations`: las observaciones de una sección en el editor,
 *     con "Marcar como revisada" (quién, cuándo y nota opcional).
 *   - `VersionObservationTrail`: el rastro para el aprobador — qué marcó la
 *     IA y quién lo revisó.
 */

import { useId, useState } from 'react'
import { Check, ChevronDown, ChevronRight, ListChecks, Loader2 } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { ApiError } from '../../api/client'
import {
  reviewAiObservation,
  undoAiObservationReview,
  versionGroundingsQueryKey,
  type GroundingFinding,
  type GroundingReport,
  type ObservationReview,
  type SectionGroundingRead,
  type SectionObservation,
} from '../../api/citations'
import { useAccessToken } from '../../auth/useAccessToken'
import { toIntlLocale } from '../../i18n/config'
import { isVersionLockError, markVersionLocked } from '../../lib/versionLock'
import { pushToast } from '../../stores/toastStore'

/** Oraciones que revisó el juez: la suma de los conteos por etiqueta. */
function verifiedTotal(report: GroundingReport): number {
  return Object.values(report.counts).reduce((sum, n) => sum + n, 0)
}

function ObservationText({ finding }: { finding: GroundingFinding }) {
  const { t } = useTranslation('ai')
  return (
    <>
      <span className="mb-1 inline-block rounded-sm bg-surface-muted px-1.5 py-0.5 font-medium text-ink-muted">
        {t(`grounding.label.${finding.label}`)}
      </span>
      <p className="text-ink">{finding.sentence}</p>
      {finding.why ? <p className="mt-1 text-ink-muted">{finding.why}</p> : null}
    </>
  )
}

export function GroundingFindingsList({ findings }: { findings: GroundingFinding[] }) {
  return (
    <ul className="space-y-2">
      {findings.map((finding, index) => (
        <li
          // Las oraciones pueden repetirse; el orden en el texto las distingue.
          key={`${index}-${finding.sentence}`}
          className="rounded-md border border-line bg-surface px-3 py-2 text-xs"
        >
          <ObservationText finding={finding} />
        </li>
      ))}
    </ul>
  )
}

/** Bloque del resultado de una corrida de IA: cuántas observaciones de
 * cuántas oraciones verificadas, una línea de contexto y la lista. Sin
 * observaciones no muestra nada. */
export function GroundingFindingsSection({ report }: { report: GroundingReport | null }) {
  const { t } = useTranslation('ai')
  if (!report || report.flagged.length === 0) return null
  const heading = t('grounding.heading', { count: report.flagged.length })
  return (
    <section aria-label={heading} className="space-y-2">
      <h3 className="flex flex-wrap items-baseline gap-x-2 text-sm font-medium text-ink">
        <ListChecks className="h-4 w-4 self-center text-brand" aria-hidden="true" />
        {heading}{' '}
        <span className="text-xs font-normal text-ink-subtle">
          {t('grounding.verified_of', { count: verifiedTotal(report) })}
        </span>
      </h3>
      <p className="text-xs text-ink-muted">{t('grounding.intro')}</p>
      <GroundingFindingsList findings={report.flagged} />
    </section>
  )
}

/** "Revisada por X · fecha" y la nota, si la hay. */
function ReviewSummary({ review }: { review: ObservationReview }) {
  const { t, i18n } = useTranslation('ai')
  const date = new Intl.DateTimeFormat(toIntlLocale(i18n.language), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(review.reviewed_at))
  return (
    <div className="mt-2 border-t border-line pt-2 text-ink-muted">
      <p className="flex items-center gap-1">
        <Check className="h-3 w-3 text-official" aria-hidden="true" />
        {review.reviewed_by_display_name
          ? t('grounding.review.reviewed_by', { name: review.reviewed_by_display_name, date })
          : t('grounding.review.reviewed_at', { date })}
      </p>
      {review.note ? <p className="mt-1 italic">{review.note}</p> : null}
    </div>
  )
}

/** Marcar como revisada (con nota opcional) o deshacer la revisión. */
function ReviewControls({
  grounding,
  observation,
  artifactId,
  versionId,
}: {
  grounding: SectionGroundingRead
  observation: SectionObservation
  artifactId: string
  versionId: string
}) {
  const { t } = useTranslation('ai')
  const { accessToken } = useAccessToken()
  const queryClient = useQueryClient()
  const [composing, setComposing] = useState(false)
  const [note, setNote] = useState('')
  const noteId = useId()

  const replaceInCache = (updated: SectionGroundingRead) => {
    queryClient.setQueryData<SectionGroundingRead[]>(
      versionGroundingsQueryKey(versionId),
      (old) => old?.map((g) => (g.id === updated.id ? updated : g)),
    )
  }
  const onError = (err: unknown) => {
    const code = err instanceof ApiError ? err.code : null
    // OT-0064 — la versión dejó de ser editable: el editor se bloquea.
    if (isVersionLockError(err)) {
      markVersionLocked(queryClient, artifactId, versionId)
    }
    if (code === 'AI_OBSERVATION_NOT_FOUND') {
      void queryClient.invalidateQueries({ queryKey: versionGroundingsQueryKey(versionId) })
    }
    const fallback = t('grounding.review.error')
    pushToast({
      kind: 'critical',
      message: code ? t(`errors:${code}`, { defaultValue: fallback }) : fallback,
    })
  }
  const target = { artifactId, versionId, groundingId: grounding.id, index: observation.index }

  const review = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return reviewAiObservation(accessToken, {
        ...target,
        sentence: observation.sentence,
        note: note.trim() || null,
      })
    },
    onSuccess: (updated) => {
      replaceInCache(updated)
      setComposing(false)
      setNote('')
    },
    onError,
  })
  const undo = useMutation({
    mutationFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return undoAiObservationReview(accessToken, target)
    },
    onSuccess: replaceInCache,
    onError,
  })

  if (observation.review) {
    return (
      <div>
        <ReviewSummary review={observation.review} />
        <button
          type="button"
          onClick={() => undo.mutate()}
          disabled={undo.isPending}
          className="mt-1 text-[11px] font-medium text-ink-subtle underline-offset-2 hover:text-ink hover:underline disabled:opacity-50"
        >
          {t('grounding.review.undo')}
        </button>
      </div>
    )
  }
  if (!composing) {
    return (
      <button
        type="button"
        onClick={() => setComposing(true)}
        className="mt-2 inline-flex items-center gap-1 rounded-md border border-line px-2 py-1 text-[11px] font-medium text-ink hover:bg-surface-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
      >
        <Check className="h-3 w-3" aria-hidden="true" />
        {t('grounding.review.mark')}
      </button>
    )
  }
  return (
    <div className="mt-2 space-y-2 border-t border-line pt-2">
      <label htmlFor={noteId} className="block text-[11px] font-medium text-ink-muted">
        {t('grounding.review.note_label')}
      </label>
      <textarea
        id={noteId}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        maxLength={500}
        rows={2}
        placeholder={t('grounding.review.note_placeholder')}
        className="block w-full rounded-md border border-line bg-canvas px-2 py-1 text-xs text-ink focus:border-brand focus:outline-none"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => {
            setComposing(false)
            setNote('')
          }}
          disabled={review.isPending}
          className="px-2 py-1 text-[11px] text-ink-muted hover:text-ink disabled:opacity-50"
        >
          {t('grounding.review.cancel')}
        </button>
        <button
          type="button"
          onClick={() => review.mutate()}
          disabled={review.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-brand px-2 py-1 text-[11px] font-medium text-canvas hover:bg-brand-deep disabled:opacity-50"
        >
          {review.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="h-3 w-3" aria-hidden="true" />
          )}
          {t('grounding.review.confirm')}
        </button>
      </div>
    </div>
  )
}

/** Observaciones de una sección en el editor: cuántas hay y cuántas faltan
 * por revisar; al desplegarlo, cada una con su revisión. Con `canReview`
 * (el editor de una versión abierta) se marcan o se deshacen. */
export function SectionObservations({
  grounding,
  artifactId,
  versionId,
  canReview,
}: {
  grounding: SectionGroundingRead
  artifactId: string
  versionId: string
  canReview: boolean
}) {
  const { t } = useTranslation('ai')
  const [open, setOpen] = useState(false)
  const listId = useId()
  const total = grounding.flagged.length
  if (total === 0) return null
  const pending = grounding.flagged.filter((f) => f.review === null).length
  return (
    <div className="space-y-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={listId}
        className="inline-flex items-center gap-1 rounded-sm bg-surface-muted px-1.5 py-0.5 text-[11px] font-medium text-ink-muted hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
      >
        {pending === 0 ? (
          <Check className="h-3 w-3 text-official" aria-hidden="true" />
        ) : (
          <ListChecks className="h-3 w-3 text-brand" aria-hidden="true" />
        )}
        {pending === 0
          ? t('grounding.all_reviewed', { count: total })
          : t('grounding.with_pending', {
              observations: t('grounding.section_badge', { count: total }),
              pending: t('grounding.pending', { count: pending }),
            })}
        {open ? (
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3 w-3" aria-hidden="true" />
        )}
      </button>
      {open ? (
        <div id={listId} className="space-y-2">
          {canReview ? (
            <p className="text-xs text-ink-muted">{t('grounding.editor_hint')}</p>
          ) : null}
          <ul className="space-y-2">
            {grounding.flagged.map((observation) => (
              <li
                key={`${observation.index}-${observation.sentence}`}
                className="rounded-md border border-line bg-surface px-3 py-2 text-xs"
              >
                <ObservationText finding={observation} />
                {canReview ? (
                  <ReviewControls
                    grounding={grounding}
                    observation={observation}
                    artifactId={artifactId}
                    versionId={versionId}
                  />
                ) : observation.review ? (
                  <ReviewSummary review={observation.review} />
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}

/** Rastro para el aprobador: cuántas observaciones dejó la verificación de
 * IA y quién las revisó; al desplegarlo, cada una por sección con su
 * revisión. Sin observaciones no muestra nada. */
export function VersionObservationTrail({
  groundings,
  sectionTitle,
}: {
  groundings: SectionGroundingRead[]
  sectionTitle: (sectionId: string, instanceKey: string) => string
}) {
  const { t, i18n } = useTranslation('ai')
  const [open, setOpen] = useState(false)
  const listId = useId()
  const sections = groundings.filter((g) => g.flagged.length > 0)
  if (sections.length === 0) return null
  const observations = sections.flatMap((g) => g.flagged)
  const pending = observations.filter((o) => o.review === null).length
  const reviewers = [
    ...new Set(
      observations
        .map((o) => o.review?.reviewed_by_display_name)
        .filter((name): name is string => !!name),
    ),
  ]
  const observationsLabel = t('grounding.section_badge', { count: observations.length })
  const summary =
    pending > 0
      ? t('grounding.trail_pending', {
          observations: observationsLabel,
          pending: t('grounding.unreviewed', { count: pending }),
        })
      : reviewers.length > 0
        ? t('grounding.trail_reviewed', {
            observations: observationsLabel,
            reviewed: t('grounding.reviewed_by_names', {
              count: observations.length,
              names: new Intl.ListFormat(toIntlLocale(i18n.language), {
                type: 'conjunction',
              }).format(reviewers),
            }),
          })
        : t('grounding.trail_title', { observations: observationsLabel })
  return (
    <div className="text-xs text-ink-muted">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={listId}
        className="inline-flex items-center gap-1.5 text-left hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand"
      >
        <ListChecks className="h-3.5 w-3.5 shrink-0 text-brand" aria-hidden="true" />
        <span>{summary}</span>
        {open ? (
          <ChevronDown className="h-3 w-3 shrink-0" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3 w-3 shrink-0" aria-hidden="true" />
        )}
      </button>
      {open ? (
        <div id={listId} className="mt-3 space-y-4">
          {sections.map((g) => (
            <section key={g.id} className="space-y-2">
              <h4 className="text-xs font-medium text-ink">
                {sectionTitle(g.section_id, g.instance_key)}
              </h4>
              <ul className="space-y-2">
                {g.flagged.map((observation) => (
                  <li
                    key={`${observation.index}-${observation.sentence}`}
                    className="rounded-md border border-line bg-surface px-3 py-2 text-xs"
                  >
                    <ObservationText finding={observation} />
                    {observation.review ? <ReviewSummary review={observation.review} /> : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      ) : null}
    </div>
  )
}
